import type { AgentWorkbenchPatch, AgentWorkSurfaceManifest } from "../../../shared/types";
import { workObjectDisplaySpec } from "./work-object-display";

export type ArtifactWorkspaceTabId = "document" | "revision" | "history" | "runtime";

export interface ArtifactWorkspaceTab {
  id: ArtifactWorkspaceTabId;
  label: string;
  active: boolean;
  secondary: boolean;
}

export interface ArtifactWorkspaceViewModel {
  objectType: AgentWorkSurfaceManifest["object_type"];
  title: string;
  typeLabel: string;
  primitiveLabel: string;
  statusLabel: string;
  provenanceLabel: string;
  manifestVersion: string;
  updatedAtLabel: string | null;
  defaultTab: ArtifactWorkspaceTabId;
  activeTab: ArtifactWorkspaceTabId;
  revisionState: "clean" | "revision_pending";
  tabs: ArtifactWorkspaceTab[];
  patchableSectionCount: number;
  sectionCount: number;
  runtimeSecondary: true;
  historySecondary: true;
}

function titleCase(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function artifactWorkspaceViewModel(
  manifest: AgentWorkSurfaceManifest,
  options: {
    provenanceLabel: string;
    proposal?: AgentWorkbenchPatch | null;
  },
): ArtifactWorkspaceViewModel {
  const proposal = options.proposal ?? null;
  const displaySpec = workObjectDisplaySpec(manifest.object_type, proposal ? "revision_pending" : manifest.status);
  const patchableSectionCount = manifest.sections.filter((section) => section.patchable).length;
  const updatedAtLabel = manifest.revision?.updated_at
    ? new Date(manifest.revision.updated_at).toLocaleDateString()
    : null;
  const activeTab: ArtifactWorkspaceTabId = proposal ? "revision" : "document";
  const tabs: ArtifactWorkspaceTab[] = [
    { id: "document", label: "Document", active: activeTab === "document", secondary: false },
    { id: "revision", label: "Revision", active: activeTab === "revision", secondary: false },
    { id: "history", label: "History", active: false, secondary: true },
    { id: "runtime", label: "Runtime", active: false, secondary: true },
  ];

  return {
    objectType: manifest.object_type,
    title: manifest.title,
    typeLabel: displaySpec.typeLabel,
    primitiveLabel: titleCase(displaySpec.primitive),
    statusLabel: titleCase(manifest.status),
    provenanceLabel: options.provenanceLabel,
    manifestVersion: manifest.manifest_version,
    updatedAtLabel,
    defaultTab: "document",
    activeTab,
    revisionState: proposal ? "revision_pending" : "clean",
    tabs,
    patchableSectionCount,
    sectionCount: manifest.sections.length,
    runtimeSecondary: true,
    historySecondary: true,
  };
}
